import { create } from '@thoughtspot/logger';
import { collectEvent, UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import * as localForage from 'localforage';
import { CacheGroupConfig, LRU_METADATA_KEY, LruCacheManager } from './lru-cache-manager';

const logger = create('StoredCacheHandler');

interface CachedItem<T> {
    data: T;
    timestamp: number;
    ttl: number;
}

const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds (fallback)
const DEFAULT_CLEANUP_INTERVAL_MS = 60 * 60 * 1000; // 1 hour in milliseconds
const DEFAULT_MAX_CACHE_SIZE_BYTES = 250 * 1024 * 1024; // 250 MB default max cache size

// Cache group constants
const CACHE_GROUP_NAMES = {
    PINBOARD: 'pinboard',
    // Future: VIZ_DETAIL: 'vizDetail',
} as const;

// Cache group-specific configurations
const PINBOARD_KEY_PATTERN = /^pinboardId-(.+?)-variables-/;
const PINBOARD_CAPACITY = 10;
const PINBOARD_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours for liveboards

// Cache group configurations (exported for testing and future extensibility)
export const CACHE_GROUPS: Record<string, CacheGroupConfig> = {
    [CACHE_GROUP_NAMES.PINBOARD]: {
        name: CACHE_GROUP_NAMES.PINBOARD,
        keyPattern: PINBOARD_KEY_PATTERN,
        extractEntityId: (key: string) => {
            const match = key.match(PINBOARD_KEY_PATTERN);
            return match ? match[1] : null;
        },
        maxEntities: PINBOARD_CAPACITY,
        ttlMs: PINBOARD_TTL_MS,
    },
    // Future: Viz Detail cache group (uncomment when needed)
    // [CACHE_GROUP_NAMES.VIZ_DETAIL]: {
    //     name: CACHE_GROUP_NAMES.VIZ_DETAIL,
    //     keyPattern: /^vizDetailId-([^-]+)-variables-/,
    //     extractEntityId: (key: string) => {
    //         const match = key.match(/^vizDetailId-([^-]+)-variables-/);
    //         return match ? match[1] : null;
    //     },
    //     maxEntities: 50,
    //     ttlMs: 60 * 60 * 1000,  // 1 hour for viz details
    // },
};

let cleanupIntervalId: ReturnType<typeof setInterval> | null = null;
let lruManagerInitialized = false;
let maxCacheSizeBytes = DEFAULT_MAX_CACHE_SIZE_BYTES;

const isExpired = (item: CachedItem<any> | null): boolean => {
    if (!item) {
        return true;
    }
    const now = Date.now();
    return now - item.timestamp > item.ttl;
};

/**
 * Estimate the size of a cache item in bytes
 * Uses JSON.stringify length as a rough estimate
 */
const estimateCacheItemSize = (item: CachedItem<any>): number => {
    try {
        return JSON.stringify(item).length * 2; // Multiply by 2 to account for UTF-16 encoding
    } catch (error) {
        logger.warn('[StoredCacheHandler] Failed to estimate cache item size:', error);
        return 0;
    }
};

/**
 * Initialize LRU manager (register cache groups and load persisted metadata)
 */
async function initializeLruManager(): Promise<void> {
    if (lruManagerInitialized) return;

    const lruManager = LruCacheManager.getInstance();

    // Register all cache groups from configuration
    Object.values(CACHE_GROUPS).forEach(config => {
        lruManager.registerCacheGroup(config);
    });

    // Load persisted metadata from IndexedDB
    await lruManager.initialize();

    lruManagerInitialized = true;
}

export const StoredCacheHandler = {
    /**
     * Retrieves cached data if it exists and hasn't expired
     * @param cachedKey - The cached key to look up
     * @returns The cached data or null if not found or expired
     */
    getCachedData: async (cachedKey: string) => {
        await initializeLruManager();

        const cachedValue = await localForage.getItem<CachedItem<any>>(cachedKey);
        if (!cachedValue) {
            return null;
        }

        try {
            const item: CachedItem<any> = cachedValue;

            if (isExpired(item)) {
                // Remove expired item
                await localForage.removeItem(cachedKey);
                return null;
            }

            // Track access for LRU (auto-detects group from key)
            const lruManager = LruCacheManager.getInstance();
            const match = lruManager.matchCacheKey(cachedKey);
            if (match) {
                lruManager.recordAccess(match.groupName, match.entityId);
            }

            return item.data;
        } catch (error) {
            // If parsing fails, remove the corrupted entry
            await localForage.removeItem(cachedKey);
            return null;
        }
    },

    /**
     * Stores data with an optional time-to-live
     * @param request - The request key
     * @param data - The data to store
     * @param ttlMs - Time to live in milliseconds (fallback if no cache group matched, default: 24 hours)
     */
    setCachedData: async (
        cachedKey: string,
        data: any,
        ttlMs: number = DEFAULT_TTL_MS,
    ) => {
        await initializeLruManager();

        const lruManager = LruCacheManager.getInstance();
        const match = lruManager.matchCacheKey(cachedKey);

        // Determine TTL: use cache group's TTL if matched, otherwise use provided/default
        let effectiveTtl = ttlMs;
        if (match) {
            const groupTtl = lruManager.getTtl(match.groupName);
            if (groupTtl !== null) {
                effectiveTtl = groupTtl;
            }
        }

        // LRU eviction logic (group-aware)
        if (match) {
            const { groupName, entityId } = match;

            if (lruManager.shouldEvict(groupName, entityId)) {
                const lruEntityId = lruManager.getLeastRecentlyUsed(groupName);
                if (lruEntityId) {
                    const currentCount = lruManager.getEntityCount(groupName);
                    const maxCapacity = lruManager.getMaxCapacity(groupName);
                    logger.debug(
                        `[StoredCacheHandler] LRU eviction triggered for group "${groupName}". ` +
                        `Current: ${currentCount}/${maxCapacity}. ` +
                        `Evicting entity: ${lruEntityId}, adding entity: ${entityId}`
                    );
                    collectEvent(
                        new UserAction(userActionTypeEnum.LRU_CACHE_LIMIT_HIT),
                        {
                            reason: 'entity-limit',
                            groupName,
                            evictedEntityId: lruEntityId,
                            newEntityId: entityId,
                            currentCount,
                            maxCapacity,
                        },
                    );
                    await StoredCacheHandler.evictEntity(groupName, lruEntityId);
                }
            }
        }

        // Store data with group-specific or default TTL
        const item: CachedItem<any> = {
            data,
            timestamp: Date.now(),
            ttl: effectiveTtl,
        };
        await localForage.setItem(cachedKey, item);

        // Update metadata
        if (match) {
            lruManager.trackCacheKey(match.groupName, match.entityId, cachedKey);
        }
    },

    /**
     * Removes cached data for a specific request
     * @param request - The request key to remove
     */
    removeCachedData: async (cachedKey: string) => {
        await initializeLruManager();

        const lruManager = LruCacheManager.getInstance();
        const match = lruManager.matchCacheKey(cachedKey);

        await localForage.removeItem(cachedKey);

        // Update metadata
        if (match) {
            lruManager.removeCacheKey(match.groupName, match.entityId, cachedKey);
        }
    },

    /**
     * Cleans up all expired entries from storage
     * @returns Number of expired entries removed
     */
    cleanupExpiredEntries: async (): Promise<number> => {
        await initializeLruManager();
        const lruManager = LruCacheManager.getInstance();
        const keys = await localForage.keys();

        const removalResults = await Promise.all(
            keys.map(async cachedKey => {
                try {
                    const cachedValue = await localForage.getItem<
                        CachedItem<any>
                    >(cachedKey);
                    if (cachedValue) {
                        const item: CachedItem<any> = cachedValue;
                        if (isExpired(item)) {
                            await localForage.removeItem(cachedKey);

                            // Update LRU metadata
                            const match = lruManager.matchCacheKey(cachedKey);
                            if (match) {
                                lruManager.removeCacheKey(match.groupName, match.entityId, cachedKey);
                            }

                            return 1;
                        }
                    }
                    return 0;
                } catch (error) {
                    // If parsing fails, remove the corrupted entry
                    await localForage.removeItem(cachedKey);

                    // Update LRU metadata
                    const match = lruManager.matchCacheKey(cachedKey);
                    if (match) {
                        lruManager.removeCacheKey(match.groupName, match.entityId, cachedKey);
                    }

                    return 1;
                }
            }),
        );

        return removalResults.reduce((sum, count) => sum + count, 0);
    },

    /**
     * Enforce total cache size limit by evicting oldest entries
     * @returns Number of entries removed
     */
    enforceSizeLimit: async (): Promise<number> => {
        await initializeLruManager();
        const lruManager = LruCacheManager.getInstance();
        const keys = await localForage.keys();

        // Calculate total cache size
        const cacheEntriesPromises = keys
            .filter(key => key !== LRU_METADATA_KEY)
            .map(async (key) => {
                try {
                    const cachedValue = await localForage.getItem<CachedItem<any>>(key);
                    if (cachedValue) {
                        const size = estimateCacheItemSize(cachedValue);
                        return {
                            key,
                            item: cachedValue,
                            size,
                            timestamp: cachedValue.timestamp,
                        };
                    }
                } catch (error) {
                    logger.warn(`[StoredCacheHandler] Error reading cache entry "${key}":`, error);
                }
                return null;
            });

        const cacheEntriesResults = await Promise.all(cacheEntriesPromises);
        const cacheEntries = cacheEntriesResults.filter((entry): entry is { key: string; item: CachedItem<any>; size: number; timestamp: number } => entry !== null);
        let totalSize = cacheEntries.reduce((sum, entry) => sum + entry.size, 0);

        // If under limit, no cleanup needed
        if (totalSize <= maxCacheSizeBytes) {
            logger.debug(
                `[StoredCacheHandler] Cache size within limit: ${(totalSize / (1024 * 1024)).toFixed(2)} MB / ${(maxCacheSizeBytes / (1024 * 1024)).toFixed(0)} MB`
            );
            return 0;
        }

        logger.info(
            `[StoredCacheHandler] Cache size exceeds limit: ${(totalSize / (1024 * 1024)).toFixed(2)} MB / ${(maxCacheSizeBytes / (1024 * 1024)).toFixed(0)} MB. Starting eviction...`
        );

        // Sort entries by timestamp (oldest first)
        cacheEntries.sort((a, b) => a.timestamp - b.timestamp);

        // Evict oldest entries until under limit
        const entriesToRemove: typeof cacheEntries = [];
        let currentSize = totalSize;
        cacheEntries.some((entry) => {
            if (currentSize <= maxCacheSizeBytes) return true;
            entriesToRemove.push(entry);
            currentSize -= entry.size;
            return false;
        });

        await Promise.all(entriesToRemove.map(async (entry) => {
            await localForage.removeItem(entry.key);
            totalSize -= entry.size;

            // Update LRU metadata
            const match = lruManager.matchCacheKey(entry.key);
            if (match) {
                lruManager.removeCacheKey(match.groupName, match.entityId, entry.key);
            }
        }));

        const removedCount = entriesToRemove.length;

        collectEvent(
            new UserAction(userActionTypeEnum.LRU_CACHE_LIMIT_HIT),
            {
                reason: 'size-limit',
                removedCount,
                totalSizeMB: +(totalSize / (1024 * 1024)).toFixed(2),
                maxSizeMB: +(maxCacheSizeBytes / (1024 * 1024)).toFixed(2),
            },
        );

        logger.info(
            `[StoredCacheHandler] Size limit enforcement complete. Removed ${removedCount} entries. ` +
            `New size: ${(totalSize / (1024 * 1024)).toFixed(2)} MB`
        );

        return removedCount;
    },

    /**
     * Clears all cached data
     */
    clearAll: async () => {
        await localForage.clear();

        // Clear LRU metadata
        await initializeLruManager();
        const lruManager = LruCacheManager.getInstance();
        lruManager.clearAll();
    },

    /**
     * Evict an entity from cache (generic)
     * @param groupName - Cache group name
     * @param entityId - Entity ID to evict
     * @returns Number of entries removed
     */
    evictEntity: async (groupName: string, entityId: string): Promise<number> => {
        const lruManager = LruCacheManager.getInstance();
        const cacheKeys = lruManager.getCacheKeys(groupName, entityId);

        if (!cacheKeys) {
            logger.debug(`[StoredCacheHandler] No cache keys found for group "${groupName}", entity "${entityId}"`);
            return 0;
        }

        const keyCount = cacheKeys.size;
        logger.debug(
            `[StoredCacheHandler] Evicting entity "${entityId}" from group "${groupName}". ` +
            `Removing ${keyCount} cache entries.`
        );

        // Delete all cache entries for this entity
        await Promise.all(
            Array.from(cacheKeys).map(key => localForage.removeItem(key)),
        );

        // Remove from metadata index
        await lruManager.removeEntity(groupName, entityId);

        logger.debug(`Successfully evicted ${keyCount} entries for entity "${entityId}"`);

        return keyCount;
    },

    /**
     * Evict a liveboard from cache (backward compatibility wrapper)
     * @param pinboardId - Pinboard ID to evict
     * @returns Number of entries removed
     */
    evictLiveboard: async (pinboardId: string): Promise<number> => {
        return StoredCacheHandler.evictEntity(CACHE_GROUP_NAMES.PINBOARD, pinboardId);
    },

    /**
     * Clears all cached data for a specific pinboard
     * @param pinboardId - The pinboard ID to clear cache for
     * @returns Number of entries removed
     */
    clearByPinboardId: async (pinboardId: string): Promise<number> => {
        await initializeLruManager();

        const prefix = `pinboardId-${pinboardId}`;
        const removed = await StoredCacheHandler.clearByPrefix(prefix);

        // Update LRU metadata
        const lruManager = LruCacheManager.getInstance();
        lruManager.removeEntity(CACHE_GROUP_NAMES.PINBOARD, pinboardId);

        return removed;
    },

    /**
     * Clears all cached data matching a key prefix
     * @param prefix - The prefix to match against cache keys
     * @returns Number of entries removed
     */
    clearByPrefix: async (prefix: string): Promise<number> => {
        await initializeLruManager();
        const lruManager = LruCacheManager.getInstance();
        const keys = await localForage.keys();
        const matchingKeys = keys.filter(key => key.startsWith(prefix));

        await Promise.all(matchingKeys.map(async key => {
            await localForage.removeItem(key);

            // Update LRU metadata
            const match = lruManager.matchCacheKey(key);
            if (match) {
                lruManager.removeCacheKey(match.groupName, match.entityId, key);
            }
        }));

        return matchingKeys.length;
    },

    /**
     * Starts periodic cleanup of expired entries and size limit enforcement
     * @param intervalMs - Cleanup interval in milliseconds (default: 1 hour)
     * @param maxSizeBytes - Maximum cache size in bytes (default: 250 MB)
     */
    startPeriodicCleanup: (
        intervalMs: number = DEFAULT_CLEANUP_INTERVAL_MS,
        maxSizeBytes: number = DEFAULT_MAX_CACHE_SIZE_BYTES,
    ) => {
        // Update max cache size
        maxCacheSizeBytes = maxSizeBytes;

        // Stop any existing cleanup interval
        if (cleanupIntervalId !== null) {
            clearInterval(cleanupIntervalId);
        }

        // Run cleanup immediately on start
        (async () => {
            await StoredCacheHandler.cleanupExpiredEntries();
        })();

        // Set up periodic cleanup
        cleanupIntervalId = setInterval(() => {
            (async () => {
                await StoredCacheHandler.cleanupExpiredEntries();
                await StoredCacheHandler.enforceSizeLimit();
            })();
        }, intervalMs);
    },

    /**
     * Stops the periodic cleanup
     */
    stopPeriodicCleanup: () => {
        if (cleanupIntervalId !== null) {
            clearInterval(cleanupIntervalId);
            cleanupIntervalId = null;
        }
    },

    /**
     * Get current cache statistics
     * @returns Cache size in bytes and number of entries
     */
    getCacheStats: async (): Promise<{ sizeBytes: number; entryCount: number; sizeMB: number }> => {
        const keys = await localForage.keys();

        const statsPromises = keys
            .filter(key => key !== LRU_METADATA_KEY)
            .map(async (key) => {
                try {
                    const cachedValue = await localForage.getItem<CachedItem<any>>(key);
                    if (cachedValue) {
                        return estimateCacheItemSize(cachedValue);
                    }
                } catch (error) {
                    // Ignore errors
                }
                return 0;
            });

        const sizes = await Promise.all(statsPromises);
        const totalSize = sizes.reduce((sum, size) => sum + size, 0);
        const entryCount = sizes.filter(size => size > 0).length;

        return {
            sizeBytes: totalSize,
            entryCount,
            sizeMB: totalSize / (1024 * 1024),
        };
    },

    /**
     * Set the maximum cache size limit
     * @param sizeBytes - Maximum cache size in bytes
     */
    setMaxCacheSize: (sizeBytes: number) => {
        maxCacheSizeBytes = sizeBytes;
    },

    /**
     * Get the current maximum cache size limit
     * @returns Maximum cache size in bytes
     */
    getMaxCacheSize: (): number => {
        return maxCacheSizeBytes;
    },

    /**
     * Export all IndexedDB cache data for debugging
     * @returns Object containing all cache entries, LRU metadata, and statistics
     */
    exportCacheData: async (): Promise<{
        cacheEntries: Record<string, any>;
        lruMetadata: any;
        statistics: {
            totalEntries: number;
            totalSizeBytes: number;
            totalSizeMB: number;
            maxSizeMB: number;
            groups: Record<string, { entityCount: number; maxEntities: number; ttlHours: number }>;
        };
        timestamp: string;
    }> => {
        await initializeLruManager();
        const lruManager = LruCacheManager.getInstance();
        const keys = await localForage.keys();

        // Collect all cache entries
        const cacheEntries: Record<string, any> = {};
        let totalSize = 0;
        let entryCount = 0;

        // Sequential iteration is intentional — each key is read one at a time to safely
        // accumulate totalSize and entryCount. forEach cannot be used as it does not await
        // async callbacks.
        // eslint-disable-next-line no-restricted-syntax
        for (const key of keys) {
            if (key === LRU_METADATA_KEY) continue;

            try {
                // eslint-disable-next-line no-await-in-loop
                const cachedValue = await localForage.getItem(key);
                if (cachedValue) {
                    cacheEntries[key] = cachedValue;
                    totalSize += estimateCacheItemSize(cachedValue as CachedItem<any>);
                    entryCount++;
                }
            } catch (error) {
                cacheEntries[key] = { error: 'Failed to read entry' };
            }
        }

        // Get LRU metadata
        const lruMetadata = await localForage.getItem(LRU_METADATA_KEY);

        // Collect group statistics
        const groupStats: Record<string, { entityCount: number; maxEntities: number; ttlHours: number }> = {};
        Object.entries(CACHE_GROUPS).forEach(([groupName, config]) => {
            groupStats[groupName] = {
                entityCount: lruManager.getEntityCount(groupName),
                maxEntities: config.maxEntities,
                ttlHours: config.ttlMs / (1000 * 60 * 60),
            };
        });

        return {
            cacheEntries,
            lruMetadata,
            statistics: {
                totalEntries: entryCount,
                totalSizeBytes: totalSize,
                totalSizeMB: totalSize / (1024 * 1024),
                maxSizeMB: maxCacheSizeBytes / (1024 * 1024),
                groups: groupStats,
            },
            timestamp: new Date().toISOString(),
        };
    },
};

// Expose debugging methods on window for browser console access
if (typeof window !== 'undefined') {
    // __debugCache uses a leading underscore to signal it is an internal debug tool
    // not intended for production use. This is a browser console utility only.
    // eslint-disable-next-line no-underscore-dangle
    (window as any).__debugCache = {
        /**
         * Export all cache data for debugging
         * Usage: await __debugCache.export()
         */
        export: async () => {
            const data = await StoredCacheHandler.exportCacheData();
            logger.info('Cache Export:', data);
            return data;
        },

        /**
         * Get cache statistics
         * Usage: await __debugCache.stats()
         */
        stats: async () => {
            const stats = await StoredCacheHandler.getCacheStats();
            const lruManager = LruCacheManager.getInstance();

            logger.info('Cache Statistics:');
            logger.info(`  Total Size: ${stats.sizeMB.toFixed(2)} MB / ${(maxCacheSizeBytes / (1024 * 1024)).toFixed(0)} MB`);
            logger.info(`  Total Entries: ${stats.entryCount}`);
            logger.info('\nPer-Group Statistics:');

            // This is a debug-only utility invoked from the browser console. Sequential
            // iteration is used to log group and entity stats in a readable order.
            // forEach cannot be used as it does not await async callbacks.
            // eslint-disable-next-line no-restricted-syntax
            for (const [groupName, config] of Object.entries(CACHE_GROUPS)) {
                const entityCount = lruManager.getEntityCount(groupName);
                logger.info(`  ${groupName}: ${entityCount}/${config.maxEntities} entities, TTL: ${config.ttlMs / (1000 * 60 * 60)}h`);

                // Show per-entity key counts and sizes
                const entityStats = lruManager.getGroupEntityStats(groupName);
                // eslint-disable-next-line no-restricted-syntax
                for (const { entityId, keyCount } of entityStats) {
                    const cacheKeys = lruManager.getCacheKeys(groupName, entityId);
                    let entitySize = 0;
                    if (cacheKeys) {
                        // eslint-disable-next-line no-restricted-syntax
                        for (const key of cacheKeys) {
                            try {
                                // eslint-disable-next-line no-await-in-loop
                                const cachedValue = await localForage.getItem<CachedItem<any>>(key);
                                if (cachedValue) {
                                    entitySize += estimateCacheItemSize(cachedValue);
                                }
                            } catch (error) {
                                // Ignore errors for individual keys
                            }
                        }
                    }
                    const entitySizeMB = entitySize / (1024 * 1024);
                    logger.info(`    - ${entityId}: ${keyCount} keys, ${entitySizeMB.toFixed(2)} MB`);
                }
            }

            return stats;
        },

        /**
         * Download cache data as JSON file
         * Usage: await __debugCache.download()
         */
        download: async () => {
            const data = await StoredCacheHandler.exportCacheData();
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `cache-export-${Date.now()}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            logger.info('Cache data downloaded');
        },

        /**
         * Clear all cache data
         * Usage: await __debugCache.clear()
         */
        clear: async () => {
            await StoredCacheHandler.clearAll();
            logger.info('Cache cleared');
        },

        /**
         * Enforce size limit manually
         * Usage: await __debugCache.enforceSizeLimit()
         */
        enforceSizeLimit: async () => {
            const removed = await StoredCacheHandler.enforceSizeLimit();
            logger.info(`Size limit enforcement complete. Removed ${removed} entries.`);
            return removed;
        },

        /**
         * Clean up expired entries
         * Usage: await __debugCache.cleanup()
         */
        cleanup: async () => {
            const removed = await StoredCacheHandler.cleanupExpiredEntries();
            logger.info(`Cleanup complete. Removed ${removed} expired entries.`);
            return removed;
        },

        /**
         * Get oldest cache time for an entity
         * Usage: __debugCache.getOldestCacheTime('pinboard', 'your-pinboard-id')
         */
        getOldestCacheTime: (groupName: string, entityId: string) => {
            const lruManager = LruCacheManager.getInstance();
            const timestamp = lruManager.getOldestCacheTime(groupName, entityId);
            if (timestamp) {
                const hoursAgo = (Date.now() - timestamp) / (1000 * 60 * 60);
                logger.info(`Oldest cache for ${groupName}/${entityId}: ${hoursAgo.toFixed(2)} hours ago`);
                return { timestamp, hoursAgo };
            }
            logger.info(`No cache found for ${groupName}/${entityId}`);
            return null;
        },

        /**
         * Set maximum cache size
         * Usage: __debugCache.setMaxSize(500) // 500 MB
         */
        setMaxSize: (sizeMB: number) => {
            StoredCacheHandler.setMaxCacheSize(sizeMB * 1024 * 1024);
            logger.info(`Max cache size set to ${sizeMB} MB`);
        },

        /**
         * Show help message with available commands
         * Usage: __debugCache.help()
         */
        help: () => {
            logger.info('Cache Debug Commands:');
            logger.info('  __debugCache.export()            - Export all cache data');
            logger.info('  __debugCache.stats()             - Show cache statistics');
            logger.info('  __debugCache.download()          - Download cache data as JSON');
            logger.info('  __debugCache.clear()             - Clear all cache data');
            logger.info('  __debugCache.enforceSizeLimit()  - Manually enforce size limit');
            logger.info('  __debugCache.cleanup()           - Clean up expired entries');
            logger.info('  __debugCache.getOldestCacheTime(group, id) - Get oldest cache time');
            logger.info('  __debugCache.setMaxSize(MB)      - Set max cache size in MB');
            logger.info('  __debugCache.help()              - Show this help message');
        },
    };

    // Log availability on load
    logger.info('[StoredCache] Debug commands available via window.__debugCache (use __debugCache.help() for list)');
}
